import { alpha, ThemeOptions } from "@mui/material/styles";
import { darken, Theme } from "@mui/material";
import type {} from "@mui/x-data-grid-premium/themeAugmentation";

export const componentOverrides: ThemeOptions["components"] = {
  MuiCard: {
    defaultProps: {
      variant: "elevation",
    },
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        backgroundColor: theme.palette.background.card,
      }),
    },
  },

  MuiTextField: {
    defaultProps: {
      variant: "outlined",
      fullWidth: true,
    },
  },

  MuiSelect: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        //make outlined input stand more out by having subtle background color
        backgroundColor:
          theme.palette.input?.background ??
          (theme.palette.mode === "light"
            ? theme.palette.common.white
            : theme.palette.common.black),
        borderRadius: 6,

        "&:hover": {
          backgroundColor: theme.palette.input?.hover,
        },

        "&.Mui-focused": {
          backgroundColor: theme.palette.input?.background,
        },

        "&.Mui-disabled": {
          backgroundColor: theme.palette.input?.disabled,
        },
      }),
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        //make outlined input stand more out by having subtle background color
        backgroundColor:
          theme.palette.input?.background ??
          (theme.palette.mode === "light"
            ? theme.palette.common.white
            : theme.palette.common.black),
        borderRadius: 6,

        "&:hover": {
          backgroundColor: theme.palette.input?.hover,
        },

        "&.Mui-focused": {
          backgroundColor: theme.palette.input?.background,
        },

        "&.Mui-disabled": {
          backgroundColor: theme.palette.input?.disabled,
        },
      }),

      input: ({ theme }: { theme: Theme }) => ({
        // autofill inputs are rendered blue...fix:
        "&:-webkit-autofill": {
          WebkitBoxShadow:
            "0 0 0 1000px var(--mui-palette-common-" +
            (theme.palette.mode === "light" ? "white" : "black") +
            ") inset",
          WebkitTextFillColor: "inherit",
          caretColor: "inherit",
          transition: "background-color 9999s ease-out", // prevents flash on autofill
        },
      }),

      notchedOutline: ({ theme }: { theme: Theme }) => ({
        borderColor: alpha(theme.palette.primary.main, 0.38),
      }),
    },
  },

  MuiFilledInput: {
    styleOverrides: {
      input: ({ theme }: { theme: Theme }) => ({
        // autofill inputs are rendered blue...fix:
        "&:-webkit-autofill": {
          WebkitBoxShadow:
            "0 0 0 1000px var(--mui-palette-common-" +
            (theme.palette.mode === "light" ? "white" : "black") +
            ") inset",
          WebkitTextFillColor: "inherit",
          caretColor: "inherit",
        },
      }),
    },
  },

  MuiDataGrid: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        borderColor: theme.palette.divider,
        "& .MuiDataGrid-main": {
          backgroundColor: theme.palette.grid?.main,
        },
        "& .MuiDataGrid-cell.row-header": {
          color: theme.palette.grid?.rowHeader?.color,
          backgroundColor: theme.palette.grid?.rowHeader?.background,
          fontWeight: theme.palette.grid?.rowHeader?.fontWeight,
          fontSize: theme.palette.grid?.rowHeader?.fontSize,
          borderRight: "1px solid var(--mui-palette-divider)",
        },
        "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeadersInner, & .MuiDataGrid-columnHeader":
          {
            backgroundColor: theme.palette.grid?.columnHeader?.background,
          },
        "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell, & .MuiDataGrid-row[aria-selected='true'] .MuiDataGrid-cell":
          {
            backgroundColor: theme.palette.grid?.dataRow?.selected,
            borderColor: darken(
              theme.palette.grid?.dataRow?.selected ?? theme.palette.divider,
              0.1
            ),
          },

        "& .MuiDataGrid-row:hover .MuiDataGrid-cell, & .MuiDataGrid-cell:hover":
          {
            backgroundColor: theme.palette.grid?.dataRow?.hover,
            borderColor: darken(
              theme.palette.grid?.dataRow?.hover ?? theme.palette.divider,
              0.1
            ),
          },
        // Pinned columns: selected hover row styling
        "& .Mui-selected.MuiDataGrid-row:hover .MuiDataGrid-cell": {
          backgroundColor: theme.palette.grid?.dataRow?.hover,
        },
      }),
    },
  },
};
